import { BookOpen, Clock, CircleHelp, CreditCard, Network, Rocket } from "lucide-react";
import type { ComponentType } from "react";
import { Link } from "react-router";
import { useDocs } from "../app/docs-context";
import { useAsync } from "../shared/lib/use-async";
import { ErrorState, LoadingState } from "../shared/ui/states";

/**
 * Category icons.
 *
 * The API stores a Lucide icon name per category, but the icon set cannot be
 * indexed dynamically without bundling all thousand-odd glyphs, so this maps the
 * handful the design uses and falls back to a book.
 */
const ICONS: Record<string, ComponentType<{ className?: string }>> = {
  rocket: Rocket,
  network: Network,
  clock: Clock,
  "credit-card": CreditCard,
  "circle-help": CircleHelp,
  book: BookOpen,
};

export function HomePage() {
  const { api, info, paths } = useDocs();
  const { data, error, loading, reload } = useAsync((signal) => api.home(signal), [api]);

  if (loading) return <LoadingState />;
  if (error || !data)
    return <ErrorState description="The documentation could not be loaded." onRetry={reload} />;

  return (
    <div className="mx-auto max-w-[1080px] px-10 pb-20 pt-12">
      <span
        className="mb-4 inline-block rounded-full px-2.5 py-1 text-sm font-medium"
        style={{ background: "var(--accent)", color: "var(--primary)" }}
      >
        Knowledge base
      </span>

      <h1
        className="mb-3 text-[2rem] font-bold leading-tight tracking-tight"
        style={{ color: "var(--foreground)" }}
      >
        {data.workspaceName}
      </h1>
      <p
        className="mb-7 max-w-[640px] text-lg leading-relaxed"
        style={{ color: "var(--muted-foreground)" }}
      >
        {info.tagline}
      </p>

      <div className="mb-14 grid gap-4 [grid-template-columns:repeat(auto-fill,minmax(268px,1fr))]">
        {data.categories.map((category) => {
          const Icon = ICONS[category.icon ?? "book"] ?? BookOpen;
          return (
            <Link
              key={category.id}
              to={paths.category(category.slug)}
              className="rounded-lg border border-border bg-card p-5 no-underline transition-colors hover:border-primary/45"
            >
              <span
                className="mb-3.5 flex size-10 items-center justify-center rounded-md"
                style={{ background: "var(--accent)", color: "var(--primary)" }}
              >
                <Icon className="size-5" />
              </span>
              <span
                className="mb-1 block text-base font-semibold"
                style={{ color: "var(--foreground)" }}
              >
                {category.name}
              </span>
              <span
                className="block text-sm leading-relaxed"
                style={{ color: "var(--muted-foreground)" }}
              >
                {category.description}
              </span>
            </Link>
          );
        })}
      </div>

      <div className="grid gap-10 lg:grid-cols-[1.6fr_1fr]">
        <div>
          <h2
            className="mb-4 text-xl font-semibold tracking-tight"
            style={{ color: "var(--foreground)" }}
          >
            Recommended articles
          </h2>
          <div
            className="flex flex-col overflow-hidden rounded-lg border"
            style={{ borderColor: "var(--border)" }}
          >
            {data.recommended.map((article) => (
              <Link
                key={article.id}
                to={paths.article(article.slug)}
                className="flex items-center gap-3 border-b px-4 py-3.5 no-underline last:border-b-0"
                style={{ background: "var(--card)", borderColor: "var(--border)" }}
              >
                <BookOpen
                  className="size-4 shrink-0"
                  style={{ color: "var(--muted-foreground)" }}
                  aria-hidden
                />
                <span className="flex-1 text-sm font-medium" style={{ color: "var(--foreground)" }}>
                  {article.title}
                </span>
                <span className="text-xs" style={{ color: "var(--muted-foreground)" }}>
                  {article.sectionName}
                </span>
              </Link>
            ))}
            {data.recommended.length === 0 && (
              <div
                className="px-4 py-6 text-sm"
                style={{ background: "var(--card)", color: "var(--muted-foreground)" }}
              >
                Articles picked by the docs team will appear here.
              </div>
            )}
          </div>
        </div>

        <div>
          <h2
            className="mb-4 text-xl font-semibold tracking-tight"
            style={{ color: "var(--foreground)" }}
          >
            Latest updates
          </h2>
          <div className="flex flex-col gap-4">
            {data.latestUpdates.map((update) => (
              <Link
                key={update.slug}
                to={paths.article(update.slug)}
                className="flex gap-3 no-underline"
              >
                <span
                  className="mt-1.5 size-2 shrink-0 rounded-full"
                  style={{ background: "var(--primary)" }}
                  aria-hidden
                />
                <span>
                  <span
                    className="block text-sm font-medium"
                    style={{ color: "var(--foreground)" }}
                  >
                    {update.title}
                  </span>
                  <span className="block text-xs" style={{ color: "var(--muted-foreground)" }}>
                    {new Date(update.publishedOn).toLocaleDateString(undefined, {
                      day: "numeric",
                      month: "short",
                      year: "numeric",
                    })}
                  </span>
                </span>
              </Link>
            ))}
            {data.latestUpdates.length === 0 && (
              <p className="text-sm" style={{ color: "var(--muted-foreground)" }}>
                Newly published articles will appear here.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
